import type { IngredientCategory } from './lib/ingredient-categories';
import type { WechatPurchaseMessage } from './lib/wechat-messages';

export type { IngredientCategory } from './lib/ingredient-categories';
export type {
  WechatMessageKind,
  WechatPurchaseMessage,
} from './lib/wechat-messages';

export type Meal = 'breakfast' | 'lunch';
export type DishType = 'normal' | 'condiment_restock';
export type PlanStatus = 'draft' | 'confirmed';

export interface IngredientLine {
  id?: string;
  name: string;
  quantity: string;
  unit: string;
  /** Older recipes omit this and are classified from their ingredient name. */
  ingredientCategory?: IngredientCategory;
}

export interface IngredientCatalogItem {
  id: string;
  name: string;
  unit: string;
  ingredientCategory: IngredientCategory;
}

export interface DailyPurchaseDefault {
  id?: string;
  name: string;
  quantity: string;
  unit: string;
  ingredientCategory: IngredientCategory;
  /**
   * Seven-bit weekday filter using UTC/JavaScript weekday indexes
   * (Sunday bit 0 through Saturday bit 6). Omitted legacy values apply daily.
   */
  weekdayMask?: number;
}

export type FixedPurchaseSourceType = 'ingredient' | 'dish' | 'custom';
export type FixedPurchaseScheduleType = 'service_day' | 'weekdays' | 'weekly';

export interface FixedPurchaseIngredientSnapshot {
  sourceType: 'ingredient';
  ingredientId: string;
  name: string;
  unit: string;
  ingredientCategory: IngredientCategory;
}

export interface FixedPurchaseCustomSnapshot {
  sourceType: 'custom';
  name: string;
  unit: string;
  ingredientCategory: IngredientCategory;
}

export interface FixedPurchaseDishSnapshot {
  sourceType: 'dish';
  dishId: string;
  dishName: string;
  type: DishType;
  category?: string;
  standardPeople: number;
  ingredients: IngredientLine[];
}

export type FixedPurchaseSourceSnapshot =
  | FixedPurchaseIngredientSnapshot
  | FixedPurchaseCustomSnapshot
  | FixedPurchaseDishSnapshot;

/**
 * A long-lived purchase rule. Rules are revisioned by the server so editing a
 * rule never rewrites a historical confirmed purchase version.
 */
export interface FixedPurchaseRule {
  id: string;
  revisionId: string;
  revision: number;
  effectiveFromWeek: string;
  active: boolean;
  sourceType: FixedPurchaseSourceType;
  sourceId?: string;
  name: string;
  unit?: string;
  ingredientCategory?: IngredientCategory;
  quantity?: string;
  batchCount?: number;
  scheduleType: FixedPurchaseScheduleType;
  weekdayMask?: number;
  sortOrder: number;
  sourceSnapshot: FixedPurchaseSourceSnapshot;
}

export interface FixedPurchaseRuleInput {
  sourceType: FixedPurchaseSourceType;
  sourceId?: string;
  name?: string;
  unit?: string;
  ingredientCategory?: IngredientCategory;
  scheduleType: FixedPurchaseScheduleType;
  weekdayMask?: number;
  quantity?: string;
  batchCount?: number;
  sortOrder?: number;
  effectiveFromWeek?: string;
  active?: boolean;
}

export interface FixedPurchaseOverride {
  ruleId: string;
  /** Present only for service-day and weekday rules. */
  date?: string;
  enabled?: boolean;
  quantity?: string;
  batchCount?: number;
}

/** A fixed rule resolved for one concrete plan week. */
export interface FixedPurchaseOccurrence {
  ruleId: string;
  revisionId: string;
  revision: number;
  date?: string;
  sourceType: FixedPurchaseSourceType;
  sourceId?: string;
  name: string;
  unit?: string;
  ingredientCategory?: IngredientCategory;
  quantity?: string;
  batchCount?: number;
  scheduleType: FixedPurchaseScheduleType;
  weekdayMask?: number;
  adjusted: boolean;
  sourceSnapshot: FixedPurchaseSourceSnapshot;
}

export interface FixedPurchaseSkipped {
  ruleId: string;
  revisionId: string;
  date?: string;
  name: string;
  reason: 'paused' | 'non_service_day' | 'week_override';
}

export interface ResolvedFixedPurchases {
  daily: FixedPurchaseOccurrence[];
  weekly: FixedPurchaseOccurrence[];
  skipped: FixedPurchaseSkipped[];
}

export interface FixedPurchaseRulesResponse {
  weekStart: string;
  defaultEffectiveFromWeek: string;
  rulesRevision: number;
  rules: FixedPurchaseRule[];
}

export interface FixedPurchaseRuleMutationResponse {
  rulesRevision: number;
  rule: FixedPurchaseRule;
}

export interface FixedPurchaseOverridesResponse {
  weekStart: string;
  planId: string;
  status: PlanStatus;
  rulesRevision: number;
  overrides: FixedPurchaseOverride[];
  resolvedFixedPurchases: ResolvedFixedPurchases;
}

export interface Dish {
  id: string;
  name: string;
  type: DishType;
  /** Optional future-facing category. Older API responses omit it and use UI fallback classification. */
  category?: string;
  standardPeople: number;
  active: boolean;
  ingredients: IngredientLine[];
}

export interface RecipeSnapshot {
  dishName: string;
  ingredients: IngredientLine[];
}

export interface PlanSelection {
  id?: string;
  dishId: string;
  meal: Meal;
  batchCount: number;
  recipeSnapshot?: RecipeSnapshot;
}

export interface PlanDay {
  date: string;
  /** Per-meal service state. Older responses may omit it and fall back to serviceEnabled. */
  mealService?: Record<Meal, boolean>;
  serviceEnabled: boolean;
  source?: 'default' | 'official' | 'manual';
  label?: string;
  selections: PlanSelection[];
}

export interface ExtraPurchaseSelection {
  id?: string;
  dishId: string;
  batchCount: number;
  recipeSnapshot?: RecipeSnapshot;
}

export type ExtraPurchaseItemSource = 'ingredient' | 'custom';

export interface ExtraPurchaseItem {
  id?: string;
  sourceType: ExtraPurchaseItemSource;
  ingredientId?: string;
  name: string;
  quantity: string;
  unit: string;
  ingredientCategory: IngredientCategory;
}

export interface PurchaseOverride {
  key: string;
  quantity: string;
}

export interface WeeklyPlan {
  id?: string;
  weekStart: string;
  weekEnd: string;
  status: PlanStatus;
  version: number;
  calendarConfigured?: boolean;
  days: PlanDay[];
  extraSelections: ExtraPurchaseSelection[];
  /** Ingredients purchased directly for this week, without creating a dish. */
  extraItems?: ExtraPurchaseItem[];
  /** Date-scoped overrides keyed by `date\0ingredient\0unit`. */
  dailyPurchaseOverrides?: Record<string, string>;
  /** Weekly extra-purchase overrides. Kept for legacy plans and non-dated purchases. */
  purchaseOverrides: Record<string, string>;
  /** Per-plan rule overrides; these never update the long-term master rule. */
  fixedPurchaseOverrides?: FixedPurchaseOverride[];
  /** Server-resolved fixed lines for this concrete week. */
  resolvedFixedPurchases?: ResolvedFixedPurchases;
  fixedPurchaseRulesRevision?: number;
  updatedAt?: string;
}

export interface PurchaseSource {
  kind: 'meal' | 'extra' | 'daily_default';
  date?: string;
  meal?: Meal;
  dishId: string;
  dishName: string;
  batchCount: number;
  contributionQuantity: string;
  unit: string;
}

export interface PurchaseItem {
  key: string;
  name: string;
  unit: string;
  calculatedQuantity: string;
  finalQuantity: string;
  ingredientCategory?: IngredientCategory;
  sources?: PurchaseSource[];
}

export interface DailyPurchaseGroup {
  date: string;
  items: PurchaseItem[];
}

export interface PurchaseAggregation {
  dailyPurchaseGroups: DailyPurchaseGroup[];
  extraPurchaseItems: PurchaseItem[];
  /** Flattened compatibility view. Daily item keys remain date-scoped. */
  purchaseItems: PurchaseItem[];
}

export interface ConfirmedSnapshot {
  planId: string;
  kitchenName: string;
  weekStart: string;
  weekEnd: string;
  version: number;
  confirmedAt?: string;
  /** New snapshots store meal purchases by service date. Older snapshots may omit this. */
  dailyPurchaseGroups?: DailyPurchaseGroup[];
  /** Purchases without a service date, such as weekly milk, eggs and condiment restocks. */
  extraPurchaseItems?: PurchaseItem[];
  /** Backward-compatible flattened payload. */
  purchaseItems: PurchaseItem[];
  selections: Array<{
    date: string;
    meal: Meal;
    dishName: string;
    batchCount: number;
    ingredients: IngredientLine[];
  }>;
  extraSelections?: Array<{
    dishName: string;
    batchCount: number;
    ingredients: IngredientLine[];
  }>;
  extraItems?: ExtraPurchaseItem[];
  wechatText: string;
  /** Frozen three-message copy. Absent on versions confirmed before this feature. */
  wechatMessages?: WechatPurchaseMessage[];
}

export interface BootstrapResponse {
  kitchenName: string;
  dishes: Dish[];
  ingredients?: IngredientCatalogItem[];
  plan: WeeklyPlan;
  calendarConfigured: boolean;
  dailyPurchaseDefaults: DailyPurchaseDefault[];
  fixedPurchaseRules?: FixedPurchaseRule[];
  fixedPurchaseRulesRevision?: number;
  fixedPurchaseDefaultEffectiveFromWeek?: string;
  latestSnapshot?: ConfirmedSnapshot;
}

export interface PublicOrder {
  kitchenName: string;
  weekStart: string;
  weekEnd: string;
  version: number;
  dailyPurchaseGroups?: DailyPurchaseGroup[];
  extraPurchaseItems?: PurchaseItem[];
  purchaseItems: PurchaseItem[];
  /** Frozen copy generated when this purchase version was confirmed. */
  wechatText?: string;
  /** Frozen three-message copy. Absent on historical versions. */
  wechatMessages?: WechatPurchaseMessage[];
  confirmedAt?: string;
}

export const MEAL_LABELS: Record<Meal, string> = {
  breakfast: '早餐',
  lunch: '中午',
};

export const DISH_TYPE_LABELS: Record<DishType, string> = {
  normal: '普通菜品',
  condiment_restock: '调味品补货',
};
